import _toConsumableArray from "/builds/huggg/sangria/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _classCallCheck from "/builds/huggg/sangria/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/builds/huggg/sangria/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
var DEFAULT_EXPIRY = 60000; // one minute

var stringifyParams = function stringifyParams() {
  for (var _len = arguments.length, params = new Array(_len), _key = 0; _key < _len; _key++) {
    params[_key] = arguments[_key];
  }
  return JSON.stringify(params);
};
var alwaysFalse = function alwaysFalse() {
  return false;
};

/**
 * This class can be used to keep some data fresh without too much hammering of
 * an API service. Should your update function require parameters, this can work
 * transparently with those, as it will memoise response by params. As a simple
 * example you might use it like this:
 *
 * ```
 * const fetchNew = () => myApi.fetch('some-data');
 *
 * const dataStore = new ExpiringCache(fetchNew);

 * dataStore.get().then(data => { [your code] });
 * ```
 *
 * This is lazy cache which will update on first read, and thereafter either
 * whenever the time expires.
 *
 * If you need more fine grained control, you can additionally pass in an expiry
 * check function. This can be useful for situations which _always_ force a
 * refresh. Such as a user logging in or out, or performing a pull-to-refresh or
 * similar. This function will be passed the params the user is using, as well
 * as the existing data in the cache.
 *
 * A more full example including a paginating endpoint and user-driven override
 * may look like this:
 *
 * ```
 * let override = false;
 *
 * someButton.addEventListener('click', () => override = true);
 *
 * const fetchNew = pageNumber => myApi.fetch('some-data', {page: pageNumber});
 *
 * // Contrived check example! arbitrarily never cache page 2 and allow user to
 * // override the caching across the board.
 * const updateCheck = (currentData, userParams) => userParams[0] === 2 || override;
 *
 * const dataStore = new ExpiringCache(fetchNew, updateCheck);

 * dataStore.get().then(data => { [your code] });
 * ```
 *
 * This example will cache all pages _except_ page 2, until the user clicks the
 * "cache-off" button
 *
 */
var ExpiringCache = /*#__PURE__*/function () {
  /**
   *
   * @param {Function} update A function to call to refresh our data on expiry
   *                          Must return a promise
   * @param {Function} isExpired [optional] A function to check any external
   *                                factors on fetch (e.g a user may have
   *                                logged in)
   * @param {int} expiry The longest time period to wait before calling update (in ms)
   * @param {Function} cacheKey A performant function to setup a unique key for the object.
   *                            If not provided we will stringify all params, buyt this can
   *                            make cache lookups slow.
   */
  function ExpiringCache(update) {
    var isExpired = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : alwaysFalse;
    var expiry = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : DEFAULT_EXPIRY;
    var cacheKey = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : stringifyParams;
    _classCallCheck(this, ExpiringCache);
    this.update = update;
    this.externalCheck = isExpired;
    this.expiry = expiry;
    this.cache = {};
    this.cacheKey = cacheKey;
  }

  /**
   * @private
   */
  _createClass(ExpiringCache, [{
    key: "_getExpiry",
    value: function _getExpiry() {
      return new Date().getTime() + this.expiry;
    }
  }, {
    key: "_setCache",
    value: function _setCache(params, cached) {
      this.cache[this.cacheKey.apply(this, _toConsumableArray(params))] = cached;
    }
  }, {
    key: "_findCache",
    value: function _findCache(params) {
      return this.cache[this.cacheKey.apply(this, _toConsumableArray(params))];
    }

    /**
     * Checks expiry time and also any external checks passed through the
     * constructor.
     *
     * @private
     */
  }, {
    key: "_shouldUpdate",
    value: function _shouldUpdate(params, cached) {
      return !cached || new Date().getTime() > cached.expiry || Boolean(this.externalCheck(cached.data, params));
    }

    /**
     * Saves data to the cache following a refresh
     *
     * @private
     */
  }, {
    key: "_updateData",
    value: function _updateData(params, data) {
      this._setCache(params, {
        data: data,
        expiry: this._getExpiry()
      });
      return data;
    }

    /**
     * Returns a promise which either resolves immediately to any value stored, or
     * refreshes the value first should the current cache be stale.
     */
  }, {
    key: "get",
    value: function get() {
      var _this = this;
      for (var _len2 = arguments.length, params = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        params[_key2] = arguments[_key2];
      }
      var cached = this._findCache(params);
      return this._shouldUpdate(params, cached) ? this.update.apply(this, params).then(function (response) {
        return _this._updateData(params, response);
      }) : Promise.resolve(cached.data);
    }
  }]);
  return ExpiringCache;
}();
export default ExpiringCache;